import { createContext, useContext } from "react";
import type { ClientTabPath } from "@ferrivia/analytics";

/** The start destination. Everything in TabNavigation is arithmetic around it. */
export const HOME: ClientTabPath = "/";

/** How the app itself initiated a tab change. A back gesture is deliberately
    not in here — nothing in the app initiates one, so it is inferred rather
    than declared (see TabNavigation's analytics effect). */
export type TabNavMethod = "tab_bar" | "in_app_link" | "nav_rail";

/** What we keep in history state. `fromHome` marks an entry this app pushed
    from the home tab, which is what makes the pop back to home safe: it means
    there is definitely a home entry underneath. Absent on an entry the user
    landed on directly (a deep link, a reload of /results). */
export interface AppHistoryState {
  fromHome?: boolean;
}

export interface TabNavigationValue {
  goToTab: (target: ClientTabPath, method: TabNavMethod) => void;
  /** Lets a panel be found again when its own tab is tapped, so the tap can
      scroll it back to the top the way a native tab bar's does. Call it from
      the panel element's `ref`; pass null on unmount. */
  registerPanel: (path: ClientTabPath, element: HTMLElement | null) => void;
}

// Split from the provider for the same reason useAppContext.ts is split from
// AppShell.tsx: a module that exports a component and a hook together breaks
// fast refresh.
export const TabNavigationContext = createContext<TabNavigationValue | null>(null);

export function useTabNavigation(): TabNavigationValue {
  const value = useContext(TabNavigationContext);
  if (!value) throw new Error("useTabNavigation must be used inside a TabNavigationProvider");
  return value;
}
